import { ContentCopy } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Grid,
  IconButton,
  ListItem,
  ListItemAvatar,
  MenuList,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Label from "src/components/Label";
import LoadingScreen from "src/components/LoadingScreen";
import { media_url } from "src/coreConfig";
import { useSelector } from "src/store";

const AccountType = ({ accountType }) => {
  const { t } = useTranslation();
  const typeVerbose = {
    savings: {
      label: t("Ahorro"),
      color: "info",
    },
    checking: {
      label: t("Corriente"),
      color: "info",
    },
  };
  return (
    <Label color={typeVerbose[accountType]?.color ?? "info"}>
      {typeVerbose[accountType]?.label ?? accountType}
    </Label>
  );
};
const PaymentAccounts = () => {
  const { t } = useTranslation();
  const { currentCustomer } = useSelector((state) => state.customers);
  const { enqueueSnackbar } = useSnackbar();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const paymentAccounts = useMemo(
    () =>
      (currentCustomer?.accounts || []).filter(
        (account) => account?.is_public && account?.transfers_enabled,
      ),
    [currentCustomer],
  );

  const handleCopyAccount = (account) => {
    navigator.clipboard.writeText(
      [account?.account_number, account?.holders_name, account?.documentId]
        .filter(Boolean)
        .join("\n"),
    );

    enqueueSnackbar(t("N�mero de cuenta copiado"), {
      variant: "success",
    });
  };

  if (!currentCustomer) {
    return <LoadingScreen message={t("Cargando componentes")} embedded />;
  }

  if (paymentAccounts.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
        {t("No se encontraron cuentas para registrar pagos en este momento.")}
      </Typography>
    );
  }

  if (isSmall) {
    return (
      <Grid container spacing={1}>
        <Grid
          size={{
            xs: 12,
          }}
        >
          <TableContainer>
            <Table
              dense
              sx={{
                "& td": {
                  padding: "4px 2px !important",
                  borderBottom: "none",
                  lineHeight: "1.15 !important",
                },
                "& > .MuiTableCell-root": {
                  padding: "4px 2px !important",
                  lineHeight: "1.15 !important",
                },
              }}
            >
              <TableBody>
                {paymentAccounts.map((a) => {
                  return [
                    <TableRow key={`${a.id || a.account_number}-head`}>
                      <TableCell
                        variant="head"
                        sx={{
                          padding: "4px 2px !important",
                          verticalAlign: "middle",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "left",
                          paddingTop: "6px !important",
                        }}
                      >
                        <Avatar
                          src={media_url(a.image)}
                          alt={a.name}
                          sx={{
                            width: {
                              xs: 34,
                              sm: 42,
                            },
                            height: {
                              xs: 34,
                              sm: 42,
                            },
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            display: "inline-block",
                            paddingLeft: "8px",
                            fontWeight: 700,
                            lineHeight: 1.1,
                          }}
                        >
                          {a.name}
                        </Typography>
                        <IconButton
                          onClick={() => handleCopyAccount(a)}
                          color="primary"
                          sx={{ ml: "auto" }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>,
                    <TableRow key={`${a.id || a.account_number}-holder`}>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {a.holders_name}
                        </Typography>
                      </TableCell>
                    </TableRow>,
                    <TableRow key={`${a.id || a.account_number}-number`}>
                      <TableCell>
                        <Typography variant="body2">
                          {a.account_number}
                        </Typography>
                      </TableCell>
                    </TableRow>,
                    <TableRow key={`${a.id || a.account_number}-doc`}>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {a.documentId}
                        </Typography>
                      </TableCell>
                    </TableRow>,
                    <TableRow
                      key={`${a.id || a.account_number}-type`}
                      sx={{
                        borderBottom: "1px solid #e0e0e0",
                        padding: "4px 2px !important",
                        marginBottom: "6px",
                      }}
                    >
                      <TableCell>
                        <AccountType accountType={a.account_type} />
                      </TableCell>
                    </TableRow>,
                  ];
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid
        size={{
          xs: 12,
        }}
      >
        <MenuList dense sx={{ py: 0 }}>
          {paymentAccounts.map((a) => {
            return (
              <ListItem
                key={`${a.id || a.account_number}-menu`}
                alignItems="flex-start"
                divider
                sx={{ py: 0.9, px: 0.5 }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={media_url(a.image)}
                    sx={{
                      width: {
                        xs: 34,
                        sm: 42,
                      },
                      height: {
                        xs: 34,
                        sm: 42,
                      },
                    }}
                  />
                </ListItemAvatar>
                <Box sx={{ ml: 1.25, flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, lineHeight: 1.15, mb: 0.2 }}
                    noWrap
                  >
                    {a.name}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                      flexWrap: "wrap",
                      mb: 0.2,
                    }}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontWeight: 500 }}
                    >
                      {a.account_number}
                    </Typography>
                    <AccountType accountType={a.account_type} />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1.2 }}
                  >
                    {a.holders_name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1.2 }}
                  >
                    {a.documentId}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => handleCopyAccount(a)}
                  color="primary"
                  edge="end"
                  sx={{ ml: 0.5 }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </ListItem>
            );
          })}
        </MenuList>
      </Grid>
      {/* {currentCustomer.accounts ? (
        currentCustomer.accounts.map((a) => {
          if (!a.is_public) {
            return null;
          }

          return (
            <Grid item xs={12} md={6} lg={4} key={`${a.id}-pa`}>
            
              <ColorCard avatarImg={a.image} title={a.name}>
                <Table
                  sx={{
                    "& td": {
                      padding: "3px !important",
                      borderBottom: "none",
                    },
                  }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell variant="head">{t("T�tular")}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{a.holders_name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head">{t("N�mero")}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{a.account_number}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head">{t("Doc ID")}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{a.documentId}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head">{t("Tipo")}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        {t(
                          a.account_type === "checking"
                            ? "Corriente"
                            : a.account_type
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </ColorCard>
            </Grid>
          );
        })
      ) : (
        <Typography>
          {t("No se encontraron cuentas para registrar pagos en este momento.")}
        </Typography>
      )} */}
    </Grid>
  );
};

export default PaymentAccounts;
